<script setup lang="ts">
/**
 * 组件接收 of Props 定义
 */
const props = defineProps<{
  /** 交易商详情数据，用于在子组件内部生成面包屑。 */
  traderData?: any
  /** 是否为黑平台，由详情页父级统一根据接口字段计算后传入。 */
  isBlackPlatform?: boolean
  /** Ultra Banner 接口返回的背景色，由详情页父级显式传入。 */
  backgroundColor?: string
}>()

const { t } = useAppI18n()
const route = useRoute()
const { getBreadcrumb } = useLsBreadcrumb()

const activeVideoUrl = ref('')
const isVideoDialogVisible = ref(false)

type BreadcrumbSourceItem = {
  route: string
  label?: string
  labelKey?: string
}

/** 当前交易商详情页对应的来源面包屑。 */
const sourceBreadcrumb = ref<BreadcrumbSourceItem | null>(null)

/** 从路由中解析当前详情页缓存 key。 */
const exchangeBreadcrumbKey = computed(() => {
  const slug = route.params.slug

  return Array.isArray(slug) ? slug[0] || '' : String(slug || '')
})

/** 按当前语言解析第二级面包屑文案。 */
const resolveSourceBreadcrumbLabel = (breadcrumb: BreadcrumbSourceItem | null) => {
  if (!breadcrumb) {
    return ''
  }

  if (breadcrumb.labelKey) {
    return t(breadcrumb.labelKey)
  }

  return breadcrumb.label || ''
}

/** 交易商展示名称：英文简称 + 不重复的本地简称。 */
const traderDisplayName = computed(() => {
  const shortName = props.traderData?.basicInfo?.shortName || ''
  const localShortName = props.traderData?.basicInfo?.localShortName || ''

  return localShortName && localShortName !== shortName ? `${shortName} ${localShortName}` : shortName
})

/** 当前页面的面包屑配置。 */
const exchangeBreadcrumbItems = computed(() => {
  const isNormalUltimate = Number(props.traderData?.basicInfo?.ultimateType) === 1
  const sourceColor = isNormalUltimate ? '#7D818A' : 'rgba(255,255,255,0.6)'
  const breadcrumbItems: Array<{ label: string; route?: string; color?: string }> = [
    {
      label: t('82520'),
      route: '/',
      color: sourceColor,
    },
  ]

  if (sourceBreadcrumb.value?.route) {
    breadcrumbItems.push({
      label: resolveSourceBreadcrumbLabel(sourceBreadcrumb.value),
      route: sourceBreadcrumb.value.route,
      color: sourceColor,
    })
  }

  breadcrumbItems.push({
    label: traderDisplayName.value,
    color: isNormalUltimate ? '#292A2E' : '#FFF',
  })

  return breadcrumbItems
})

/** 当前交易商是否为 Ultra 旗舰店。 */
const isUltraUltimate = computed(() => Number(props.traderData?.basicInfo?.ultimateType) === 3)

/** Ultra Banner 的响应式图片数据。 */
const ultraWebImages = computed(() => props.traderData?.ultra?.banner?.webImages)
const viewportWidth = ref(0)

const updateViewportWidth = () => {
  viewportWidth.value = window.innerWidth || document.documentElement.clientWidth
}

/** 读取有效的视频地址，兼容接口返回字符串或带 URL 字段的对象。 */
const interviewVideoUrl = computed(() => {
  if (!isUltraUltimate.value) {
    return ''
  }

  const interviewVideo = props.traderData?.interviewVideo

  if (typeof interviewVideo === 'string') {
    return interviewVideo.trim()
  }

  if (interviewVideo && typeof interviewVideo === 'object') {
    const videoUrl = interviewVideo.path || interviewVideo.url || interviewVideo.videoUrl || interviewVideo.address

    return typeof videoUrl === 'string' ? videoUrl.trim() : ''
  }

  return ''
})

/** 当前视频是否使用 ViewDetails 的 MP4 预览模式。 */
const isMp4Video = computed(() => interviewVideoUrl.value.toLowerCase().includes('.mp4'))

/** 将 Ultra 背景图片宽度映射转换为 srcset 可用的图片列表。 */
const ultraBackgroundSources = computed(() => {
  if (!isUltraUltimate.value) {
    return []
  }

  const webImages = ultraWebImages.value

  if (!webImages || typeof webImages !== 'object') {
    return []
  }

  const entries = Array.isArray(webImages)
    ? webImages.map((image) => {
        const imageData = image && typeof image === 'object' ? image : {}

        return {
          width: imageData.width || imageData.imageWidth || imageData.screenWidth || imageData.w,
          image: imageData,
        }
      })
    : Object.entries(webImages).map(([width, image]) => ({ width, image }))

  return entries
    .map(({ width, image }) => ({
      width: Number(width),
      url:
        typeof image === 'string'
          ? image.trim()
          : String(
              image?.content || image?.url || image?.path || image?.src || image?.image || image?.webImage || '',
            ).trim(),
    }))
    .filter((item) => Number.isFinite(item.width) && item.width > 0 && item.url)
    .toSorted((first, second) => first.width - second.width)
})

/** 是否存在可用于覆盖默认背景的 Ultra 响应式图片。 */
const hasUltraBackgroundImages = computed(() => ultraBackgroundSources.value.length > 0)

/** 根据浏览器可视宽度选择 Ultra Banner 图片尺寸。 */
const ultraBackgroundTargetWidth = computed(() => {
  if (viewportWidth.value >= 1200) {
    return 1240
  }

  if (viewportWidth.value >= 1000) {
    return 960
  }

  return 780
})

/** 当前浏览器可视宽度对应的 Ultra Banner 图片。 */
const selectedUltraBackground = computed(() => {
  const sources = ultraBackgroundSources.value
  const targetSource = sources.find((source) => source.width === ultraBackgroundTargetWidth.value)

  return targetSource || sources[0]
})

/** 将 Hex 颜色转换为 rgba，其他颜色格式保持不变。 */
const toRgbaColor = (color: string) => {
  const hexMatch = color.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i)

  if (!hexMatch) {
    return color
  }

  const hex = hexMatch[1] || ''
  const red = Number.parseInt(hex.length === 3 ? `${hex[0]}${hex[0]}` : hex.slice(0, 2), 16)
  const green = Number.parseInt(hex.length === 3 ? `${hex[1]}${hex[1]}` : hex.slice(2, 4), 16)
  const blue = Number.parseInt(hex.length === 3 ? `${hex[2]}${hex[2]}` : hex.slice(4, 6), 16)

  return `rgba(${red},${green},${blue},1)`
}

/** Ultra Banner 背景色，保持接口返回的颜色格式。 */
const ultraBannerBackground = computed(() => {
  const background = props.backgroundColor || props.traderData?.ultra?.banner?.background
  return typeof background === 'string' && background.trim() ? background.trim() : '#271847'
})

/** 仅用于图片遮罩渐变的 rgba 背景色。 */
const ultraBannerBackgroundRgba = computed(() => {
  return toRgbaColor(ultraBannerBackground.value)
})

/** Ultra 背景图片的响应式 srcset。 */
const ultraBackgroundSrcset = computed(() =>
  ultraBackgroundSources.value.map((source) => `${source.url} ${source.width}w`).join(', '),
)

/** Ultra 背景图片在不支持 srcset 时使用的兜底地址。 */
const ultraBackgroundFallbackUrl = computed(() => selectedUltraBackground.value?.url || '')

/** 打开视频弹窗并从当前视频地址开始播放。 */
const openVideoDialog = () => {
  if (!interviewVideoUrl.value) {
    return
  }

  activeVideoUrl.value = interviewVideoUrl.value
  isVideoDialogVisible.value = true
}

/** 同步视频弹窗状态并在关闭后清理视频地址。 */
const handleVideoDialogVisibility = (visible: boolean) => {
  isVideoDialogVisible.value = visible

  if (!visible) {
    activeVideoUrl.value = ''
  }
}

/** 读取当前交易商详情页的来源面包屑。 */
onMounted(() => {
  sourceBreadcrumb.value = getBreadcrumb(exchangeBreadcrumbKey.value)
  updateViewportWidth()
  window.addEventListener('resize', updateViewportWidth)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', updateViewportWidth)
})

/**
 * 根据 ultimateType 计算不同的 class 类名
 * 1: 普通旗舰店, 2: 钻石旗舰店, 3: Ultra旗舰店, 4: Lite旗舰店
 */

const heroSubClass = computed(() => {
  const basicInfo = props.traderData?.basicInfo
  const ultimateType = Number(basicInfo?.ultimateType)
  // Ultra 有两种场景：有背景图 URL / 无 URL
  if (ultimateType === 3) {
    if (hasUltraBackgroundImages.value) {
      return 'exchange-detail-hero-ultra-image'
    }

    return basicInfo?.backgroundUrl ? 'exchange-detail-hero-ultra-url' : 'exchange-detail-hero-ultra'
  }

  const classMap: Record<number | string, string> = {
    1: 'exchange-detail-hero-normal',
    2: 'exchange-detail-hero-diamond',
    4: 'exchange-detail-hero-lite',
  }

  const matchedClass = classMap[ultimateType]
  if (matchedClass) {
    return matchedClass
  }

  // 黑平台。
  if (props.isBlackPlatform) {
    return 'exchange-detail-hero-black'
  }

  return 'exchange-detail-hero-base'
})
</script>

<template>
  <section
    :class="['exchange-detail-hero', heroSubClass]"
    :style="isUltraUltimate ? { background: ultraBannerBackground } : undefined"
  >
    <div
      v-if="isUltraUltimate && hasUltraBackgroundImages"
      class="absolute inset-0 mx-auto w-[1240px] max-w-full overflow-hidden"
      aria-hidden="true"
    >
      <!-- ponytail: 使用原生 img 原因：需要 srcset/sizes 响应式切换，CommonImage 不支持这些属性。 -->
      <img
        :src="ultraBackgroundFallbackUrl"
        :srcset="ultraBackgroundSrcset"
        sizes="100vw"
        class="size-full object-cover"
        alt=""
      />
      <div
        class="absolute inset-0"
        :style="{
          background: `linear-gradient(180deg, rgba(39,25,71,0.7) 0%, ${ultraBannerBackgroundRgba} 100%)`,
        }"
      ></div>
    </div>

    <div class="exchange-detail-container pointer-events-none relative z-[1] h-[126px]">
      <CommonBreadcrumb class="pointer-events-auto" :items="exchangeBreadcrumbItems" variant="light" />
    </div>

    <button
      v-if="isUltraUltimate && interviewVideoUrl"
      type="button"
      class="absolute top-[83px] left-1/2 z-[2] flex size-[44px] -translate-x-1/2 cursor-pointer items-center justify-center rounded-full bg-[rgba(41,42,46,0.9)] p-[8px] transition-colors hover:bg-[rgba(41,42,46,1)]"
      aria-label="Play interview video"
      @click="openVideoDialog"
    >
      <span
        class="ml-[2px] size-0 border-t-[7px] border-b-[7px] border-l-[11px] border-t-transparent border-b-transparent border-l-[#ffffff]"
      ></span>
    </button>

    <CommonVideoPlayer
      v-if="isUltraUltimate && !isMp4Video"
      v-model="isVideoDialogVisible"
      :src="activeVideoUrl"
      title="Interview video"
      @update:model-value="handleVideoDialogVisibility"
      @close="handleVideoDialogVisibility(false)"
    />

    <CommonViewDetails
      v-else-if="isUltraUltimate"
      v-model="isVideoDialogVisible"
      :address="activeVideoUrl"
      @update:model-value="handleVideoDialogVisibility"
      @closevideo="handleVideoDialogVisibility(false)"
    />
  </section>
</template>

<style scoped lang="less">
.exchange-detail-hero {
  position: relative;
  padding-top: 70px;
}

.exchange-detail-hero-base {
  background: linear-gradient(180deg, #4088d8 0%, #7db8fb 100%);
}

.exchange-detail-hero-normal {
  // 普通旗舰店样式
  position: relative;
  background: linear-gradient(180deg, #ffe07e 0%, #ffedb3 100%);

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: url('https://resources1.interface003.com/web30/2026/06/22/ddc5c2ba-0d88-4370-9d47-39f55f3b8327.png')
      center / cover;
    opacity: 0.6;
  }
}

.exchange-detail-hero-diamond {
  // 钻石旗舰店样式
  background:
    url('https://resources1.interface003.com/web30/2026/06/22/288585df-b277-4744-bc34-a0def6fac474.gif') center / cover,
    linear-gradient(180deg, #06606b 0%, #06606b 100%),
    linear-gradient(180deg, #01444d 0%, #6ba2a9 100%);
}

.exchange-detail-hero-lite {
  // Lite旗舰店样式
  background: linear-gradient(180deg, #142864 0%, #1c2d87 100%);
}

.exchange-detail-hero-ultra {
  // Ultra旗舰店 - 无背景图 URL
  background:
    url('https://resources1.interface003.com/web30/2026/07/13/1966d18a-e75a-43fd-bd6e-b2f17577e242.gif') center / cover,
    #271847;
}

.exchange-detail-hero-ultra-image {
  background: #271847;
}

.exchange-detail-hero-ultra-url {
  // Ultra旗舰店 - 有背景图 URL
  background:
    url('https://resources1.interface003.com/web30/2026/07/11/26aab907-0920-400b-a2fb-b9e9872c864a.png') center / cover,
    linear-gradient(180deg, #271947 70%, #271947 100%),
    #271847;
}

.exchange-detail-hero-black {
  // 黑平台样式
  background: linear-gradient(180deg, #000 0%, #282828 100%);
}

.exchange-detail-container {
  width: min(1200px, calc(100vw - 48px));
  margin: 0 auto;
}

@media screen and (max-width: 799px) {
  .exchange-detail-container {
    width: min(1200px, calc(100vw - 32px));
  }
}
</style>
